import {
  Component,
  type ElementRef,
  inject,
  Input,
  NgZone,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AccountService } from "app/shared/services/account.service";
import { ProvisioningService } from "app/shared/services/provisioning.service";
import { StorageService } from "app/shared/services/storage.service";
import { UrbanSitterService } from "app/shared/services/urban-sitter.service";
import { environment } from "environments/environment";
import type { AnimationItem } from "lottie-web/build/player/lottie_light";
import lottie from "lottie-web/build/player/lottie_light";

@Component({
  selector: "app-loading-state",
  templateUrl: "./loading-state.component.html",
  styleUrls: ["./loading-state.component.scss"],
  standalone: false,
})
export class LoadingStateComponent {
  @Input() id = "hearts-loader";
  animation: AnimationItem;
  @ViewChild("loader") loader: ElementRef;
  redirectUrl = "";

  readonly #routerSnapshot = inject(ActivatedRoute);
  readonly #storage = inject(StorageService);
  readonly #urbanSitterService = inject(UrbanSitterService);
  readonly #provisioningService = inject(ProvisioningService);
  readonly #accountService = inject(AccountService);
  readonly #ngZone = inject(NgZone);

  constructor() {
    this.redirectUrl =
      this.#routerSnapshot.snapshot.queryParams?.redirectUrl ?? "";
  }

  ngAfterViewInit(): void {
    this.animation = lottie.loadAnimation({
      container: this.loader.nativeElement,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: "assets/loading-hearts.json",
    });
    this.validateLocalStorage();
  }

  ngOnDestroy(): void {
    this.animation.destroy();
  }

  navigateTo(url: string) {
    window.location.assign(url);
  }

  validateLocalStorage() {
    if (
      this.redirectUrl === "" &&
      this.#routerSnapshot.snapshot.queryParams?.urbanSitter === undefined
    ) {
      return;
    }

    const intervalId = setInterval(() => {
      const token = this.#storage.getItem("access_token");
      if (token === null || token === "") {
        return;
      }

      if (this.#routerSnapshot.snapshot.queryParams?.urbanSitter) {
        clearInterval(intervalId);
        this.ssoToUrbanSitter();
        return;
      }

      if (this.redirectUrl !== "") {
        clearInterval(intervalId);
        this.navigateTo(this.redirectUrl);
      }
    }, 1000);
  }

  async ssoToUrbanSitter() {
    const account = this.#accountService.getLogged();
    if (account instanceof Error) {
      throw account;
    }

    const org = await this.#accountService
      .getAccountOrg(account.id)
      .toPromise();
    if (
      !org.hasUrbanSitter ||
      account.benefitId ||
      !(account.country === "US" || !account.country)
    ) {
      this.navigateTo(environment.AUTH_URL);
    }
    this.#provisioningService.getProvisioningByOrg(org.id).subscribe((res) => {
      if (res.urbanSitterMemberSSO?.value) {
        this.#ngZone.runOutsideAngular(() => {
          this.#urbanSitterService
            .connectAccount()
            .subscribe(({ token, refreshToken }) => {
              const urbanSitterForm =
                this.#urbanSitterService.createUrbanSitterForm(
                  token,
                  refreshToken,
                );
              urbanSitterForm.submit();
            });
        });
      } else {
        this.navigateTo(environment.AUTH_URL);
      }
    });
  }
}
